import { ColumnConfig } from "@app/shared/waffles";
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { SortDirections, WafflesPaginationProps } from "@app/shared/waffles/exports";
import ExceptionsService from "@app/exceptions/exceptionsService";
import {
  EditExceptionModel,
  BulkSpecialtyGroupException,
  ExceptionsTableData,
  ExceptionsFilterRequestModel
} from "@app/exceptions/interfaces";
import moment from "moment";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { FlyoutLink } from "@app/shared/Flyouts";
import EditExceptionFlyout from "@app/exceptions/EditExceptionFlyout";
import ExceptionsFilters from "@app/exceptions/ExceptionsFilters";
import {
  SpecialtyGroupExceptionHistoryTimeline
} from "@app/projects/network-assessment/SpecialtyGroupExceptionHistoryTimeline";
import ArchiveExceptionsModal from "@app/exceptions/ArchiveExceptionsModal";
import ExpireModal from "@app/exceptions/ExpireModal";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import ExceptionsBadge from "@app/exceptions/ExceptionsBadge";
import startCase from "lodash/startCase";
import {
  SortExceptionColumn,
  SpecialtyGroupExceptionReviewStatusLabels,
  SpecialtyGroupExceptionGrade,
  SpecialtyGroupExceptionStatus
} from "@app/exceptions/enums";
import useShowSharedInsightsForClient
  from "@app/projects/network-assessment/adequacy/hooks/useShowSharedInsightsForClient";
import MetricsBox from "@app/shared/metrics/MetricsBox";
import MetricsBars from "@app/shared/metrics/MetricsBars";
import MetricsStackedBar from "@app/shared/metrics/MetricsStackedBar";
import MetricsPie from "@app/shared/metrics/MetricsPie";
import {
    getGapSharedInsightsMessage,
    getExceptionSharedInsightsPreviousYearApprovalsMessage,
    getExceptionSharedInsightsInProgressMessage,
    validateSharedInsights
} from "@app/exceptions/sharedInsightsMessages";
import { SpecialtyGroupExceptionReviewStatusFilterOption } from "@app/exceptions/specialtyGroupExceptionReviewStatusFilterOptionEnum";
import ExceptionsExport from "@app/exceptions/ExceptionsExport";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { formatDateTime } from "@app/shared/formatDateTime";
import PageSection from "@app/shared/PageSection";

interface ExceptionsDataGridProps {
  isArchivedTable: boolean;
}

export const ExceptionsOverview: FC<ExceptionsDataGridProps> = ({
                                                                  isArchivedTable
                                                                }: ExceptionsDataGridProps) => {
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const showSharedInsightsForClient = useShowSharedInsightsForClient();
  const exceptionsService = useRef(new ExceptionsService()).current;
  const queryClient = useQueryClient();
  const gapFillStatusDescriptions = useGapFillStatusDescriptions();
  const requestModel = exceptionFilterContext.requestModel;
  const requestModelKey = useMemo(() => JSON.stringify(requestModel), [requestModel]);

  const { data: exceptionsData, isFetching: loading } = useQuery({
    enabled: requestModel != null && requestModel.projectIds.length > 0 && exceptionFilterContext.isForArchived === isArchivedTable,
    queryFn: ({ signal }) => exceptionsService.getExceptionsData(requestModel, signal),
    queryKey: ["exceptions-data", isArchivedTable, requestModelKey],
    // Rapid tab switching aborts in-flight requests for keys that are abandoned before ever
    // resolving; react-query has no prior data to revert to in that case, so it surfaces the
    // abort as a real error. Retrying it would just re-send the already-abandoned request and
    // can leave a stale query stuck fetching in the background. Don't retry — a fresh fetch is
    // already underway under the new (active) key.
    retry: false
  });

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [clickedExceptionId, setClickedExceptionId] = useState<number>();
  const [isExpireModalOpen, setIsExpireModalOpen] = useState(false);

  const handleOpenArchiveModal = (exceptionId: number) => {
    setIsArchiveModalOpen(true);
    setClickedExceptionId(exceptionId);
  }

  const handleOpenExpireModal = (exceptionId: number) => {
    setIsExpireModalOpen(true);
    setClickedExceptionId(exceptionId);
  }

  const paginationProps: WafflesPaginationProps = useMemo<WafflesPaginationProps>(() => {
    const wafflesPaginationProps: WafflesPaginationProps = {
      changePage: (pageNumber: number) => handlePageChange(pageNumber),
      enableBackendPagination: true,
      handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
      pageSizeOptions: [10, 100, 1000],
      recordsPerPage: exceptionFilterContext.pageSize,
      resetToFirstPage: exceptionFilterContext.resetToFirstPage,
      showPageSizeSelector: true,
      totalRecords: (exceptionsData?.totalCount ?? 0)
    }

    return wafflesPaginationProps;
  }, [exceptionFilterContext.pageSize, exceptionFilterContext.resetToFirstPage, exceptionsData?.totalCount]);

  const handlePageSizeChange = (pageSize: number) => {
    exceptionFilterContext.setPageSize(pageSize);
    exceptionFilterContext.setPageIndex(1);
  }

  const handlePageChange = (newPage: number) => {
    if (!exceptionFilterContext.resetToFirstPage) {
      exceptionFilterContext.setPageIndex(newPage);
    }
    if (exceptionFilterContext.resetToFirstPage) {
      exceptionFilterContext.setResetToFirstPage(false);
    }
  };

  const handleCheckboxChange = (selectedKeys: string[]) => {
    // Handle selecting elements
    if (selectedKeys.length > exceptionFilterContext.selectedExceptionIds.length) {
      exceptionFilterContext.setSelectedExceptionIds((prevSelectedIds) => {
        const newSelectedIds = [...prevSelectedIds];
        selectedKeys.forEach((exceptionId) => {
          if (!newSelectedIds.includes(parseInt(exceptionId, 10))) {
            const parsedId = parseInt(exceptionId, 10);
            if (!newSelectedIds.includes(parsedId)) {
              newSelectedIds.push(parsedId);
            }
          }
        });
        return newSelectedIds;
      });
    }

    // Handle unselecting elements
    if (selectedKeys.length < exceptionFilterContext.selectedExceptionIds.length) {
      exceptionFilterContext.setSelectedExceptionIds(prevSelectedIds =>
        prevSelectedIds.filter(exceptionId => selectedKeys.includes(exceptionId.toString()))
      );
    }

    // Set the custom project names and enableRegulatoryExceptionReports based on the selected exceptions
    if (selectedKeys.length > 0) {
      const selectedExceptionIds = selectedKeys.map(key => parseInt(key, 10));
      const selectedProjects = exceptionsTableData.filter(data => selectedExceptionIds.includes(data.exceptionId));

      const projectNames: string[] = [];
      const enableRegulatoryExceptionReports: boolean[] = [];
      const haveRemovedServiceArea: boolean[] = [];
      const haveRemovedSpecialty: boolean[] = [];
      const isCciioFlags: boolean[] = [];

      selectedProjects.forEach(project => {
        projectNames.push(project.projectName.props.children);
        enableRegulatoryExceptionReports.push(project.enableRegulatoryExceptionReports);
        haveRemovedServiceArea.push(project.hasRemovedServiceArea);
        haveRemovedSpecialty.push(project.hasRemovedSpecialty);
        isCciioFlags.push(project.isCciio);
      });

      exceptionFilterContext.setCustomProjectName(projectNames);
      exceptionFilterContext.setEnableRegulatoryExceptionReports(enableRegulatoryExceptionReports);
      exceptionFilterContext.setHaveRemovedServiceArea(haveRemovedServiceArea);
      exceptionFilterContext.setHaveRemovedSpecialty(haveRemovedSpecialty);
      exceptionFilterContext.setSelectedIsCciioFlags(isCciioFlags);
    } else {
      exceptionFilterContext.setCustomProjectName([]);
      exceptionFilterContext.setEnableRegulatoryExceptionReports([]);
      exceptionFilterContext.setHaveRemovedServiceArea([]);
      exceptionFilterContext.setHaveRemovedSpecialty([]);
      exceptionFilterContext.setSelectedIsCciioFlags([]);
    }
  }

  const customSort = (data: ExceptionsTableData[], column: keyof ExceptionsTableData, direction: SortDirections) => {
    exceptionFilterContext.setSortDirection(direction);

    switch (column) {
      case "projectName":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.CustomProjectName);
        break;
      case "serviceArea":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.ServiceArea);
        break;
      case "serviceAreaStatuses":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.RegionSetStatus);
        break;
      case "specialty":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.Specialty);
        break;
      case "gapResolutionStatus":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.GapResolutionStatus);
        break;
      case "grade":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.Grade);
        break;
      case "metOverall":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.MetOverall);
        break;
      case "startDate":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.StartDate);
        break;
      case "endDate":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.EndDate);
        break;
      case "status":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.SpecialtyGroupExceptionStatus);
        break;
      case "previouslyApproved":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.PreviouslyApproved);
        break;
      case "comments":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasComments);
        break;
      case "narrative":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasNarrative);
        break;
      case "processingStatus":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.ProcessingStatus);
        break;
      case "updatedBy":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.UpdatedByUserFullName);
        break;
      case "lineOfBusiness":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.LineOfBusiness);
        break;
      case "hasUnprocessedExcludedProviders":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.HasUnprocessedExcludedProviders);
        break;
      case "reviewStatus":
        exceptionFilterContext.setSortColumn(SortExceptionColumn.ReviewStatus);
        break;
      case "lastUpdated":
      default:
        exceptionFilterContext.setSortColumn(SortExceptionColumn.DateUpdated);
    }

    // Data is undefined and not used here due to passThroughSorting being set to true, but we have to return it either way.
    return data;

  }

  useEffect(() => {
    if (exceptionFilterContext.requestModel == null && exceptionFilterContext.isForArchived && !isArchivedTable) {
      exceptionFilterContext.setIsForArchived(false);
    }
    if (exceptionFilterContext.requestModel == null && !exceptionFilterContext.isForArchived && isArchivedTable) {
      exceptionFilterContext.setIsForArchived(true);
    }
  }, [exceptionFilterContext.requestModel, exceptionFilterContext.isForArchived]);

  // Actions that mutate exceptions (archive/expire/edit/generate document) signal a refresh here without
  // changing requestModel, so the query key wouldn't otherwise change. Only the active tab's query is
  // refetched — react-query only refetches invalidated queries that currently have a mounted observer.
  useEffect(() => {
    if (!exceptionFilterContext.triggerRefreshData || requestModel == null) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["exceptions-data"] })
      .finally(() => exceptionFilterContext.setTriggerRefreshData(false));
  }, [exceptionFilterContext.triggerRefreshData, requestModel]);

  const refreshData = () => exceptionFilterContext.setTriggerRefreshData(true);

  const handleAttachmentsDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  }

  const getGapSharedInsightsTableMessage = (exception: BulkSpecialtyGroupException) : string => {
    const gapsCount = exception.gapSharedInsightsCount ?? 0;
    const payersCount = exception.gapSharedInsightsPayerCount ?? 0;
    const validationMessage = validateSharedInsights(exception.hasProjectExceptionPackage, gapsCount);
    return validationMessage != null
        ? validationMessage
        : `${getGapSharedInsightsMessage(gapsCount, payersCount)}.`;
  }

  const getSharedInsightsPreviousYearApprovalsTableMessage = (exception: BulkSpecialtyGroupException) : string =>
      getSharedInsightsExceptionTableMessage(exception.hasProjectExceptionPackage, exception.exceptionSharedInsightsPreviousYearApprovalsCount ?? 0, true);

  const getSharedInsightsInProgressTableMessage = (exception: BulkSpecialtyGroupException) : string =>
      getSharedInsightsExceptionTableMessage(exception.hasProjectExceptionPackage, exception.exceptionSharedInsightsInProgressCount ?? 0, false);

  const getSharedInsightsExceptionTableMessage = (hasProjectExceptionPackage: boolean, count: number, isPreviousYear: boolean) : string => {
      const validationMessage = validateSharedInsights(hasProjectExceptionPackage, count);
      return validationMessage != null
        ? validationMessage
        : (isPreviousYear
            ? getExceptionSharedInsightsPreviousYearApprovalsMessage(count)
            : getExceptionSharedInsightsInProgressMessage(count));
  }

  const exceptionsTableData: ExceptionsTableData[] = useMemo<ExceptionsTableData[]>(() => {
    let data: ExceptionsTableData[] = [];

    if (exceptionsData) {
      /* oxlint-disable sort-keys */
      data = exceptionsData?.items?.map((exception) => {
        const customProjectId = exception.customProjectId;
        const customProjectName = exception.customProjectName;
        const exceptionId = exception.specialtyGroupExceptionId;
        const serviceArea = `${exception.regionSetDescription}, ${exception.regionSetStateAbbreviation}`;
        const serviceAreaStatuses = exception.regionSetStatus == null ? '' : exception.regionSetStatus;
        const specialty = exception.specialtyGroupName;
        const specialtyGroupCode = exception.specialtyGroupCode;
        const gapResolutionStatus = gapFillStatusDescriptions[exception.gapResolutionStatus];
        const status = exception.specialtyGroupExceptionStatus;
        const startDate = exception.startDate ? moment(exception.startDate).format("MM/DD/YYYY") : '---';
        const endDate = exception.endDate ? moment(exception.endDate).format("MM/DD/YYYY") : '---';
        const previouslyApproved = exception.previouslyApproved;
        const enableRegulatoryExceptionReports = exception.enableRegulatoryExceptionReports;
        const hasRemovedServiceArea = exception.regionSetStatus == null;
        const hasRemovedSpecialty = exception.specialtyGroupName === "";
        const exceptionSharedInsightsPreviousYearApprovalsCount = exception.exceptionSharedInsightsPreviousYearApprovalsCount ?? 0;
        const exceptionSharedInsightsInProgressCount = exception.exceptionSharedInsightsInProgressCount ?? 0;
        const gapSharedInsightsCount = exception.gapSharedInsightsCount ?? 0;
        const gapSharedInsightsPayerCount = exception.gapSharedInsightsPayerCount ?? 0;

        const exceptionSharedInsightsPreviousYearApprovals = getSharedInsightsPreviousYearApprovalsTableMessage(exception);
        const exceptionSharedInsightsInProgressApprovals = getSharedInsightsInProgressTableMessage(exception);
        const gapSharedInsights = getGapSharedInsightsTableMessage(exception);

        const editExceptionModel: EditExceptionModel = {
          customProjectId,
          customProjectName,
          exceptionId,
          serviceArea,
          specialty,
          specialtyGroupCode,
          status,
          startDate,
          endDate,
          previouslyApproved,
          hasProjectExceptionPackage: exception.hasProjectExceptionPackage,
          exceptionSharedInsightsPreviousYearApprovalsCount,
          exceptionSharedInsightsInProgressCount,
          excludeFromCCIIOJustification: exception.excludeFromCCIIOJustification,
          gapSharedInsightsCount,
          gapSharedInsightsPayerCount
        };

        return {
          key: exception.specialtyGroupExceptionId.toString(),
          exceptionId: exception.specialtyGroupExceptionId,
          projectName: (
            <a
              href={`${window.customUIBaseUrl}/projects/${exception.customProjectId}/adequacy/specialty/${exception.specialtyGroupCode}/county/${exception.regionSetCode}`}
              target="_blank" rel="noreferrer">
              {exception.customProjectName}
            </a>
          ),
          serviceArea: `${exception.regionSetDescription}, ${exception.regionSetStateName} - ${exception.regionSetCode}`,
          specialty: `${specialty} (${specialtyGroupCode})`,
          serviceAreaStatuses,
          lineOfBusiness: exception.lineOfBusiness,
          gapResolutionStatus: gapResolutionStatus,
          reviewStatus: (<ExceptionsBadge status={exception.reviewStatus}>{SpecialtyGroupExceptionReviewStatusLabels[exception.reviewStatus]}</ExceptionsBadge>),
          metOverall: exception.metCriteria != null ? exception.metCriteria ? 'Yes' : 'No' : '---',
          startDate: exception.startDate ? moment(exception.startDate).format("MM/DD/YYYY") : '---',
          endDate: exception.endDate ? moment(exception.endDate).format("MM/DD/YYYY") : '---',
          hasUnprocessedExcludedProviders: exception.hasUnprocessedExcludedProviders ? 'Yes' : 'No',
          status: exception.specialtyGroupExceptionStatus.charAt(0).toUpperCase() + exception.specialtyGroupExceptionStatus.slice(1),
          gapSharedInsights,
          exceptionSharedInsightsPreviousYearApprovals,
          exceptionSharedInsightsInProgressApprovals,
          grade: exception.grade === SpecialtyGroupExceptionGrade.None ? '---' : startCase(exception.grade),
          previouslyApproved: exception.previouslyApproved ? 'Yes' : 'No',
          comments: exception.hasComments ? 'Yes' : 'No',
          narrative: exception.hasNarrative ? 'Yes' : 'No',
          processingStatus: exception.isUnprocessed ? 'Unprocessed' : 'Processed',
          attachment: (
            <ul className="exceptions__attachment-list">
              {exception?.supplementaryDocuments && exception.supplementaryDocuments.map(file =>
                <li key={file.supplementaryDocumentMetadataId}>
                  <a href="#"
                     onClick={() => handleAttachmentsDownload(file.supplementaryDocumentMetadataId)}>{file.documentName}</a>
                </li>
              )}
            </ul>
          ),
          lastUpdated: formatDateTime(exception.dateUpdated),
          updatedBy: exception.updatedByUserFullName,
          enableRegulatoryExceptionReports: enableRegulatoryExceptionReports,
          hasRemovedServiceArea: hasRemovedServiceArea,
          hasRemovedSpecialty: hasRemovedSpecialty,
          isCciio: exception.isCciio,
          actions: (
            <NewActionMenu
              buttonClassNames="p-0 text-dark btn-icon btn-sm"
              testId="exception__action-menu"
              containerClassnames="exceptions__more-actions-container"
              hideArrow={true}
              fixMenu={true}
              fixedMenuOffset={{
                x: -210,
                y: 18,
              }}
              buttonText={<i className="fa fa-ellipsis-v"/>}
            >
              {isArchivedTable ?
                (<FlyoutLink
                  classNames="d-block action-menu-item fw-normal col-12"
                  content={{
                    body: <EditExceptionFlyout
                      editException={editExceptionModel}
                      exceptionStatusOptions={exceptionFilterContext.exceptionStatusOptions}
                      refreshData={refreshData}
                      isForArchived={true}
                    />,
                    title: `Exception: ${customProjectName}`,
                  }}
                  size="40"
                  testId={`exception__reactivate-${exceptionId}`}
                >
                  Reactivate
                </FlyoutLink>)
                : (<>
                  <FlyoutLink
                    classNames="d-block action-menu-item fw-normal col-12"
                    content={{
                      body: <EditExceptionFlyout
                        editException={editExceptionModel}
                        exceptionStatusOptions={exceptionFilterContext.exceptionStatusOptions}
                        refreshData={refreshData}
                        isForArchived={false}
                      />,
                      title: `Exception: ${customProjectName}`,
                    }}
                    size="40"
                    testId={`exception__edit-${exceptionId}`}
                  >
                    Edit
                  </FlyoutLink>
                  {exception.specialtyGroupExceptionStatus !== "expired" &&
                    <NewActionMenuItem
                      display={"Expire"}
                      onClick={() => handleOpenExpireModal(exceptionId)}
                      testId={`exception__expire-${exceptionId}`}
                      isSelected={false}
                    />
                  }
                  <NewActionMenuItem
                    display="Archive"
                    isSelected={false}
                    onClick={() => handleOpenArchiveModal(exceptionId)}
                    testId={`exception__archive-${exceptionId}`}
                  />
                </>)}
              <FlyoutLink
                classNames="d-block action-menu-item fw-normal col-12"
                content={{
                  body: <SpecialtyGroupExceptionHistoryTimeline
                    regionSetSpecialtyGroupId={exception.regionSetSpecialtyGroupId}
                    projectId={exception.customProjectId}/>,
                  title: "View History",
                }}
                testId="specialty-group-exception-editor__exception-history-flyout"
              >
                View History
              </FlyoutLink>
            </NewActionMenu>
          )
        };
      });
      /* oxlint-enable sort-keys */
    }
    return data;
  }, [exceptionsData, featureFlags]);

  const exceptionsColumnConfig: ColumnConfig = useMemo<ColumnConfig>(() => {
    const showCommunityExceptionTooltips = exceptionsData?.items?.some(exception => exception.hasProjectExceptionPackage) ?? false;
    const communityExceptionTooltip = showCommunityExceptionTooltips ?
      "The data used in this column comes from client-reported information." : null;

    return {
      actions: {
        displayText: "",
        minWidth: "114%",
        noSort: true,
        sizeContentToFit: true,
        testId: "exception-action"
      },
      attachment: {
        displayText: "Attachment",
        hidden: isArchivedTable,
        minWidth: "75px",
        noSort: true,
        sizeContentToFit: true,
        testId: "exception-attachment",
      },
      comments: {
        displayText: "Comments",
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-duration"
      },
      endDate: {
        displayText: "Expiration Date",
        minWidth: "90px",
        sizeContentToFit: true,
        testId: "exception-end-date",
      },
      exceptionSharedInsightsInProgressApprovals: {
        displayText: "Shared Insights: Exceptions - In Progress Reviews",
        hidden: !showSharedInsightsForClient,
        noSort: true,
        sizeContentToFit: true,
        testId: "exception-market-insights-inprogress-approvals",
        tooltip: communityExceptionTooltip,
        wrapContent: true,
      },
      exceptionSharedInsightsPreviousYearApprovals: {
        displayText: "Shared Insights: Exceptions - Previous Year Approvals",
        hidden: !showSharedInsightsForClient,
        noSort: true,
        sizeContentToFit: true,
        testId: "exception-market-insights-previous-year-approvals",
        tooltip: communityExceptionTooltip,
        wrapContent: true,
      },
      gapResolutionStatus: {
        displayText: "Gap Resolution Status",
        sizeContentToFit: true,
        testId: "exception-gap-resolution-status",
      },
      gapSharedInsights: {
        displayText: "Shared Insights: Gaps",
        hidden: !showSharedInsightsForClient,
        minWidth: "350px",
        noSort: true,
        sizeContentToFit: true,
        testId: "exception-gap-market-insights",
        tooltip: showCommunityExceptionTooltips
          ? "The data in this column comes from payer networks in QES using data from the most recent published time slice. Only gaps in counties with an active county status are displayed."
          : null,
        wrapContent: true,
      },
      grade: {
        displayText: "Exception Grade",
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-grade",
        tooltip: "A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.\n" +
          "B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n" +
          "C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.",
      },
      hasUnprocessedExcludedProviders: {
        displayText: "Unprocessed Excluded Providers",
        sizeContentToFit: true,
        testId: "exception-unprocessed-excluded-providers",
        tooltip: "This column indicates if an exception currently has unprocessed excluded providers."
      },
      lastUpdated: {
        displayText: isArchivedTable ? "Archived Date" : "Last Updated",
        sizeContentToFit: true,
        testId: "exception-last-updated",
      },
      lineOfBusiness: {
        displayText: "Line of Business",
        sizeContentToFit: true,
        testId: "exception-line-of-business",
      },
      metOverall: {
        displayText: "Met Overall",
        sizeContentToFit: true,
        testId: "exception-met-overall",
      },
      narrative: {
        displayText: "Narrative",
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-narrative",
      },
      previouslyApproved: {
        displayElement: <span>Previously<br/>Approved</span>,
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-previously-approved",
      },
      processingStatus: {
        displayText: "Processing Status",
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-processing-status",
      },
      projectName: {
        displayText: "Project",
        minWidth: "150px",
        sizeContentToFit: true,
        testId: "exception-project",
      },
      reviewStatus: {
        displayText: "Internal Review Status",
        minWidth: "150px",
        sizeContentToFit: true,
        testId: "exception-review-status",
        tooltip: "This status is for the internal exception review process.",
      },
      serviceArea: {
        displayText: "Service Area - County SSA",
        minWidth: "150px",
        sizeContentToFit: true,
        testId: "exception-service-area",
      },
      serviceAreaStatuses: {
        displayText: "Service Area Status",
        minWidth: "150px",
        sizeContentToFit: true,
        testId: "exception-service-area-status",
      },
      specialty: {
        displayText: "Specialty",
        sizeContentToFit: true,
        testId: "exception-specialty",
      },
      startDate: {
        displayText: "Start Date",
        minWidth: "90px",
        sizeContentToFit: true,
        testId: "exception-start-date",
      },
      status: {
        displayText: "Exception Status",
        hidden: isArchivedTable,
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-status",
      },
      updatedBy: {
        displayText: isArchivedTable ? "Archived By" : "Updated By",
        minWidth: "100px",
        sizeContentToFit: true,
        testId: "exception-updated-by",
      },
    };
  }, [exceptionsTableData]);

  const startRecord: number = useMemo<number>(() => {
    if (exceptionFilterContext.pageIndex != undefined && exceptionFilterContext.pageSize != undefined) {
      return (exceptionFilterContext.pageIndex - 1) * exceptionFilterContext.pageSize + 1;
    }
  }, [exceptionFilterContext.pageIndex, exceptionFilterContext.pageSize]);

  const endRecord: number = useMemo<number>(() => {
    if (exceptionsData?.totalCount != undefined) {
      return Math.min(exceptionFilterContext.pageIndex * exceptionFilterContext.pageSize, exceptionsData?.totalCount);
    }
  }, [exceptionFilterContext.pageIndex, exceptionFilterContext.pageSize, exceptionsData?.totalCount]);

  const applyExceptionFilters = (exceptionsFilterRequestModel: ExceptionsFilterRequestModel) => {
    exceptionFilterContext.setSelectedExceptionIds([]);
    exceptionFilterContext.setPageIndex(1);
    exceptionFilterContext.setResetToFirstPage(true);
    exceptionFilterContext.applyFilters(exceptionsFilterRequestModel);
  };

  const applyExceptionGradesFilter = (grade: SpecialtyGroupExceptionGrade) => {
    const currentExceptionsFilter = exceptionFilterContext.getExceptionsFilter();

    exceptionFilterContext.setFilterExceptionGrades([{ key: grade, name: startCase(grade) }]);
    applyExceptionFilters({ ...currentExceptionsFilter, specialtyGroupExceptionGrades: [grade] });
  };

  const applyExceptionReviewStatusFilter = (reviewStatus: SpecialtyGroupExceptionReviewStatusFilterOption) => {
    const currentExceptionsFilter = exceptionFilterContext.getExceptionsFilter();

    exceptionFilterContext.setFilterReviewStatus(reviewStatus);
    applyExceptionFilters({ ...currentExceptionsFilter, reviewStatus });
  };

  const applyExceptionStatusFilter = (exceptionStatus: SpecialtyGroupExceptionStatus) => {
    const currentExceptionsFilter = exceptionFilterContext.getExceptionsFilter();

    exceptionFilterContext.setFilterExceptionStatus([{ key: exceptionStatus, name: startCase(exceptionStatus) }]);
    applyExceptionFilters({ ...currentExceptionsFilter, specialtyGroupExceptionStatus: [exceptionStatus] });
  };

  return (
    <PageSection className="exceptions-overview">
      <ExceptionsFilters/>
      {!isArchivedTable && (<ExceptionsExport exceptionsData={exceptionsData} isLoading={loading} />)}
      {(startRecord != undefined && endRecord != undefined) &&
        <p>Displaying {exceptionsData?.totalCount > 0 ? `${startRecord}-${endRecord} of ${exceptionsData?.totalCount}` : "0"} records</p>
      }
      {!isArchivedTable &&
        <AccordionContainer>
          <Accordion
            testId="exceptions-overview__key-metrics-accordion"
            title="Key Metrics"
            defaultOpen={true}
          >
            <div className="exceptions-overview__metrics-container">
              <MetricsBox label="Total" value={exceptionsData?.totalCount ?? 0} sublabel="Exceptions" />
              <MetricsBars label="Internal Review Status" bars={[
                {
                  color: "#cc3a52",
                  label: "Not Ready",
                  onClick: () => applyExceptionReviewStatusFilter(SpecialtyGroupExceptionReviewStatusFilterOption.NotReady),
                  testId: "exceptions-overview__bar-not-ready",
                  value: exceptionsData?.notReady ?? 0
                },
                {
                  color: "#ffd654",
                  label: "Ready for Review",
                  onClick: () => applyExceptionReviewStatusFilter(SpecialtyGroupExceptionReviewStatusFilterOption.ReadyForReview),
                  testId: "exceptions-overview__bar-ready-for-review",
                  value: exceptionsData?.readyForReview ?? 0
                },
                {
                  color: "#008559",
                  label: "Ready for Submission",
                  onClick: () => applyExceptionReviewStatusFilter(SpecialtyGroupExceptionReviewStatusFilterOption.ReadyForSubmission),
                  testId: "exceptions-overview__bar-ready-for-submission",
                  value: exceptionsData?.readyForSubmission ?? 0
                }
              ]} />
              <MetricsStackedBar label="Exception Status" segments={[
                {
                  label: "Pending",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Pending),
                  testId: "exceptions-overview__segment-pending",
                  value: exceptionsData?.pending ?? 0
                },
                {
                  label: "Submitted",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Submitted),
                  testId: "exceptions-overview__segment-submitted",
                  value: exceptionsData?.submitted ?? 0
                },
                {
                  label: "Denied",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Denied),
                  testId: "exceptions-overview__segment-denied",
                  value: exceptionsData?.denied ?? 0
                },
                {
                  label: "Appealed",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Appealed),
                  testId: "exceptions-overview__segment-appealed",
                  value: exceptionsData?.appealed ?? 0
                },
                {
                  label: "Approved",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Approved),
                  testId: "exceptions-overview__segment-approved",
                  value: exceptionsData?.approved ?? 0
                },
                {
                  label: "Expired",
                  onClick: () => applyExceptionStatusFilter(SpecialtyGroupExceptionStatus.Expired),
                  testId: "exceptions-overview__segment-expired",
                  value: exceptionsData?.expired ?? 0
                }
              ]} />
              <MetricsPie label="Exception Grades" segments={[
                {
                  color: "#008559",
                  label: "Grade A (High)",
                  onClick: () => applyExceptionGradesFilter(SpecialtyGroupExceptionGrade.A),
                  testId: "exceptions-overview__pie-grade-a",
                  value: exceptionsData?.gradeA ?? 0
                },
                {
                  color: "#ffd654",
                  label: "Grade B (Med)",
                  onClick: () => applyExceptionGradesFilter(SpecialtyGroupExceptionGrade.B),
                  testId: "exceptions-overview__pie-grade-b",
                  value: exceptionsData?.gradeB ?? 0
                },
                {
                  color: "#cc3a52",
                  label: "Grade C (Low)",
                  onClick: () => applyExceptionGradesFilter(SpecialtyGroupExceptionGrade.C),
                  testId: "exceptions-overview__pie-grade-c",
                  value: exceptionsData?.gradeC ?? 0
                }
              ]} />
            </div>
          </Accordion>
        </AccordionContainer>
      }
      <Waffles<ExceptionsTableData>
        columns={exceptionsColumnConfig}
        passThroughSorting={true}
        customSort={customSort}
        data={exceptionsTableData || []}
        enablePagination={true}
        handleCheckboxChange={handleCheckboxChange}
        loading={loading}
        paginationProps={paginationProps}
        selectedKeys={exceptionFilterContext.selectedExceptionIds.map(num => num.toString())}
        showCheckboxes={!isArchivedTable}
        testId="exceptions-waffles-table"
      />
      <ArchiveExceptionsModal
        isModalOpen={isArchiveModalOpen}
        setIsModalOpen={setIsArchiveModalOpen}
        singleExceptionId={clickedExceptionId}
        resetSelectedCheckboxes={() => {
          exceptionFilterContext.setSelectedExceptionIds([])
        }}
      />
      <ExpireModal
        isModalOpen={isExpireModalOpen}
        setIsModalOpen={setIsExpireModalOpen}
        singleExceptionId={clickedExceptionId}
        resetSelectedCheckboxes={() => {
          exceptionFilterContext.setSelectedExceptionIds([])
        }}
      />
    </PageSection>
  )
}